import { TimeMS } from '../utilities/time';

export interface StorageBackend {
    setItem(key: string, item: unknown, type?: string): void;
    getItem<T>(key: string, type?: string): T;
    removeItem(key: string): void;
    clear(): void;
    getAllKeys(): Promise<string[]>;
    getMultipleItems(keys: string[]): [[string, unknown]];
}

const defaultCacheDurationMS = 15 * TimeMS.minute;
const defaultCacheCleaningFrequencyMS = TimeMS.day;
const defaultMinItemsToClean = 100;

export interface CacheManagerOptions {
    defaultCacheDurationMS?: number;
    isEnabled?: boolean;
    storageBackend: StorageBackend;
    enableMemoryCache?: boolean;
    minItemsToClean?: number;
    cacheCleaningFrequencyMS?: number;
}

interface CacheData {
    data: unknown;
    cacheExpiry?: number;
}

// Cache Separator allows for easy parsing of cache keys from cache ids
const cacheSeparator = '_';

// Cache prefix is used for pulling determining what items were created by this cache manager
const cachePrefix = 'wbCache-';

// Last time that the cache was cleaned
const lastCleaningTimeUTCCacheKey = 'lastStorageCleaningTimeUTC';

export class CacheManager {
    cacheMissMap: Map<string, (syncParams: unknown)=>Promise<unknown>>;
    defaultCacheDurationMS: number;
    isEnabled: boolean;
    storageBackend: StorageBackend;
    enableMemoryCache: boolean;
    memoryCache: Map<string, unknown>;
    // Minimum number of items for cleaning/scavenging to be determined as necessary
    minItemsToClean: number;
    // Number of milliseconds since the last time a cleaning/scavenging occurred before it is necessary to do again
    cacheCleaningFrequencyMS: number;


    constructor(options: CacheManagerOptions = { storageBackend: undefined }) {
        // Tracks our functions that we call to look data up when a cache miss occurs
        this.cacheMissMap = new Map();
        this.defaultCacheDurationMS = options.defaultCacheDurationMS ?? defaultCacheDurationMS;
        this.isEnabled = options.isEnabled ?? true;
        this.storageBackend = options.storageBackend;
        this.enableMemoryCache = options.enableMemoryCache ?? false;
        this.minItemsToClean = options.minItemsToClean ?? defaultMinItemsToClean;
        this.cacheCleaningFrequencyMS = options.cacheCleaningFrequencyMS ?? defaultCacheCleaningFrequencyMS;

        if (!options.storageBackend
            || !options.storageBackend.setItem
            || !options.storageBackend.getItem
            || !options.storageBackend.removeItem) {
            console.log('Cache Manager - no storageBackend was specified or storage backend was invalid so data will not be properly persisted. Memory cache will be enabled and used by default.');
            this.enableMemoryCache = true;
        }

        if (this.enableMemoryCache) {
            this.memoryCache = new Map();
        }
    }

    addCacheMissCallback(cacheKey: string, callback: ()=>Promise<unknown>): void {
        if (!cacheKey) {
            console.warn('CacheManager.addCacheMissCallback - cache key must be specified.');
            return;
        }

        if (!callback) {
            console.warn('CacheManager.addCacheMissCallback - callback function must be specified.', cacheKey);
            return;
        }

        this.cacheMissMap.set(cacheKey, callback);
    }

    async load(cacheKey: string, cacheId = '', syncParams: unknown): Promise<unknown> {
        if (!cacheKey) {
            console.warn('CacheManager.load - no cache key was specified');
            return;
        }

        if (!this.isEnabled) {
            // call the lookup function immediately
            return this._cacheLookup(cacheKey, cacheId, syncParams);
        }

        const fullCacheKey = this._getFullCacheKey(cacheKey, cacheId);

        let currentResult;

        if (this.enableMemoryCache && this.memoryCache.has(fullCacheKey)) {
            currentResult = this.memoryCache.get(fullCacheKey);
        } else if (this.storageBackend?.getItem) {
            currentResult = this.storageBackend?.getItem(fullCacheKey);
        }

        if (currentResult && (!currentResult.cacheExpiry || currentResult.cacheExpiry > Date.now())) {
            // data was in cache and either has no cache expiration or was not stale
            return Promise.resolve(currentResult.data);
        } else {
            // data was not in cache or was stale
            return this._cacheLookup(cacheKey, cacheId, syncParams);
        }
    }

    async _cacheLookup(cacheKey: string, cacheId: string, syncParams: unknown): Promise<unknown> {
        if (this.cacheMissMap.has(cacheKey)) {
            const callback = this.cacheMissMap.get(cacheKey);
            return await callback({
                id: cacheId,
                syncParams
            });
        } else {
            console.error('Cache miss occurred for item with no cache miss callback configured: ' + cacheKey);
        }
    }

    save(cacheKey: string, cacheId = '', dataToCache: unknown, cacheDuration = this.defaultCacheDurationMS): void {
        if (!this.isEnabled) {
            return;
        }

        const fullCacheKey = this._getFullCacheKey(cacheKey, cacheId);
        // always save with a cache expiry unless undefined/null is specifically passed which allows for defining items that never expire
        const cacheExpiry = cacheDuration && Date.now() + cacheDuration;
        const cacheObject: CacheData = { data: dataToCache, cacheExpiry };
        if (this.enableMemoryCache) {
            this.memoryCache.set(fullCacheKey, cacheObject);
        }

        if (this.storageBackend?.setItem) {
            this.storageBackend.setItem(fullCacheKey, cacheObject, 'object');
        }
    }

    remove(cacheKey: string, cacheId = ''): void {
        const fullCacheKey = this._getFullCacheKey(cacheKey, cacheId);
        if (this.enableMemoryCache) {
            this.memoryCache.delete(fullCacheKey);
        }

        if (this.storageBackend?.removeItem) {
            this.storageBackend.removeItem(fullCacheKey);
        }
    }

    async clear(clearEntireStorageBackend?: boolean): Promise<void> {
        if (this.enableMemoryCache) {
            this.memoryCache.clear();
        }

        if (clearEntireStorageBackend && this.storageBackend?.clear) {
            this.storageBackend.clear();
        } else {
            if (this.storageBackend.getAllKeys && this.storageBackend.removeItem) {
                const allKeys = await this.storageBackend.getAllKeys();
                // Only delete keys created by the cache manager
                if(allKeys && allKeys.length > 0) {
                    allKeys.forEach((key) => {
                        if (key.startsWith(cachePrefix)) {
                            this.storageBackend.removeItem(key);
                        }
                    });
                }
            }
        }
    }

    async cleanExpiredItems(): Promise<void> {
        if (!this.isEnabled) {
            console.log('CacheManager.cleanExpiredItems was called but caching is disabled');
            return;
        }

        if (this.enableMemoryCache) {
            const lastCleaningTimeUTC = this.memoryCache.get(lastCleaningTimeUTCCacheKey) as number;
            if (!lastCleaningTimeUTC || !this.cacheCleaningFrequencyMS || Date.now() - lastCleaningTimeUTC > this.cacheCleaningFrequencyMS) {
                if (!this.minItemsToClean || this.memoryCache.size >= this.minItemsToClean) {
                    this.memoryCache.forEach((currentItem: CacheData, key) => {
                        if (currentItem?.cacheExpiry && currentItem.cacheExpiry < Date.now()) {
                            this.memoryCache.delete(key);
                        }
                    });
                }

                this.memoryCache.set(lastCleaningTimeUTCCacheKey, Date.now());
            }
        }

        if (this.storageBackend
            && this.storageBackend.getAllKeys
            && this.storageBackend.removeItem
            && this.storageBackend.getItem) {
            const lastCleaningTimeUTC = this.storageBackend.getItem(lastCleaningTimeUTCCacheKey, 'number') as number;
            if (!lastCleaningTimeUTC || !this.cacheCleaningFrequencyMS || Date.now() - lastCleaningTimeUTC > this.cacheCleaningFrequencyMS) {
                const allKeys = await this.storageBackend.getAllKeys();
                if (!this.minItemsToClean || allKeys.length > this.minItemsToClean) {
                    allKeys.forEach(key => {
                        const currentItem = this.storageBackend.getItem<CacheData>(key, 'object');
                        if (currentItem?.cacheExpiry && currentItem.cacheExpiry < Date.now()) {
                            this.storageBackend.removeItem(key);
                        }
                    });
                }
            }

            this.storageBackend.setItem(lastCleaningTimeUTCCacheKey, Date.now(), 'number');
        }
    }

    _getFullCacheKey(cacheKey: string, cacheId = ''): string {
        return `${cachePrefix}${cacheKey}${cacheSeparator}${cacheId}`;
    }
}
